<script setup>
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  BarElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  BarController,
  LineController
} from 'chart.js'
import { computed, defineProps } from 'vue'

ChartJS.register(
  LineController,
  BarController,
  LineElement,
  PointElement,
  BarElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend
)

const props = defineProps({
  historySalePrices: Array,
  salePrice: Number
})

const getDayStart = value => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

const dailySeries = computed(() => {
  const buckets = new Map()

  for (const item of props.historySalePrices || []) {
    const dayStart = getDayStart(item.time)
    if (!dayStart) continue

    const key = dayStart.getTime()
    const bucket = buckets.get(key) || {
      timestamp: key,
      label: dayStart.toLocaleDateString(),
      count: 0,
      prices: []
    }

    bucket.count++
    const price = Number(item.price)
    if (Number.isFinite(price)) bucket.prices.push(price)
    buckets.set(key, bucket)
  }

  const todayStart = getDayStart(new Date())
  if (todayStart && !buckets.has(todayStart.getTime())) {
    buckets.set(todayStart.getTime(), {
      timestamp: todayStart.getTime(),
      label: todayStart.toLocaleDateString(),
      count: 0,
      prices: []
    })
  }

  return Array.from(buckets.values()).sort((a, b) => a.timestamp - b.timestamp)
})

const labels = computed(() => dailySeries.value.map(item => item.label))

const volumeByDay = computed(() => {
  const todayStart = getDayStart(new Date())
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  const yesterdayStart = getDayStart(yesterday)
  const highlightedDays = new Set([todayStart?.getTime(), yesterdayStart?.getTime()])

  return dailySeries.value.map(item => ({
    value: item.count,
    highlight: highlightedDays.has(item.timestamp) && item.count < 2
  }))
})

const medianPrice = prices => {
  if (!prices.length) return null

  const sorted = [...prices].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)

  return sorted.length % 2 ? sorted[middle] : Math.round((sorted[middle - 1] + sorted[middle]) / 2)
}

const medianPriceByDay = computed(() => dailySeries.value.map(item => medianPrice(item.prices)))

const chartData = computed(() => ({
  labels: labels.value,
  datasets: [
    {
      type: 'bar',
      data: volumeByDay.value.map(v => v.value),
      yAxisID: 'volume',
      barThickness: 6,
      backgroundColor: volumeByDay.value.map(v =>
        v.highlight ? 'rgba(255,0,0,0.2)' : 'rgba(255,255,255,0.08)'
      )
    },
    {
      type: 'line',
      label: 'Медианная цена',
      data: medianPriceByDay.value,
      borderWidth: 3,
      tension: 0.35,
      pointRadius: 4,
      pointHoverRadius: 7,
      pointHitRadius: 20,
      borderColor: '#60a5fa',
      pointBackgroundColor: '#60a5fa'
    },
    {
      type: 'line',
      data: labels.value.map(() => props.salePrice),
      borderDash: [6, 6],
      borderWidth: 1,
      pointRadius: 0,
      borderColor: '#888',
      fill: {
        above: 'rgba(34,197,94,0.12)'
      }
    }
  ]
}))

const options = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false
  },
  plugins: {
    legend: { display: false }
  },
  scales: {
    x: { display: false },
    y: {
      position: 'right',
      ticks: {
        color: '#888',
        callback: v => v / 1_000_000 + 'm'
      },
      grid: {
        color: 'rgba(255,255,255,0.05)'
      }
    },
    volume: {
      position: 'left',
      display: false,
      min: 0
    }
  }
}
</script>

<template>
  <div style="height: 100px; width: 400px">
    <Line :data="chartData" :options="options" />
  </div>
</template>
